import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { SessionProvider, useSession } from "@/context/SessionContext";
import { AppShell } from "@/components/layout/AppShell";
import { Toaster } from "@/components/ui/sonner";
import { ErrorState } from "@/components/common/States";
import AuthPage from "@/pages/AuthPage";
import NotProvisioned from "@/pages/NotProvisioned";
import Dashboard from "@/pages/Dashboard";
import ModulePlaceholder from "@/pages/ModulePlaceholder";
import CompaniesPage from "@/pages/admin/CompaniesPage";
import BranchesPage from "@/pages/admin/BranchesPage";
import UsersPage from "@/pages/admin/UsersPage";
import RolesPage from "@/pages/admin/RolesPage";
import AuditPage from "@/pages/admin/AuditPage";
import MasterList from "@/components/master/MasterList";
import EntityDetail from "@/components/master/EntityDetail";
import { MASTERS } from "@/lib/masters";
import { QuotationList, QuotationDetail } from "@/pages/ops/Quotations";
import { JobCardList, JobCardDetail } from "@/pages/ops/JobCards";
import { ApprovalCenter, ApprovalPolicies } from "@/pages/ops/Approvals";
import GarageVehicles from "@/pages/garage/GarageVehicles";
import ServiceRecords from "@/pages/garage/ServiceRecords";
import ServiceRecordDetail from "@/pages/garage/ServiceRecordDetail";
import { DueList, VehicleTimeline } from "@/pages/garage/ServiceTimeline";
import Stock from "@/pages/garage/Stock";
import Expenses from "@/pages/garage/Expenses";
import ServiceApprovals from "@/pages/garage/ServiceApprovals";
import SettingsPage from "@/pages/SettingsPage";

function SessionGate() {
  const { loading, me, error, reload } = useSession();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }
  if (error) return <div className="min-h-screen flex items-center justify-center"><ErrorState message={error} onRetry={reload} /></div>;
  if (!me.provisioned) return <NotProvisioned />;
  return (
    <AppShell>
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="admin/companies" element={<CompaniesPage />} />
        <Route path="admin/branches" element={<BranchesPage />} />
        <Route path="admin/users" element={<UsersPage />} />
        <Route path="admin/roles" element={<RolesPage />} />
        <Route path="admin/audit" element={<AuditPage />} />
        <Route path="admin/approval-policies" element={<ApprovalPolicies />} />
        <Route path="quotations" element={<QuotationList />} />
        <Route path="quotations/:id" element={<QuotationDetail />} />
        <Route path="job-cards" element={<JobCardList />} />
        <Route path="job-cards/:id" element={<JobCardDetail />} />
        <Route path="approvals" element={<ApprovalCenter />} />
        <Route path="garage-vehicles" element={<GarageVehicles />} />
        <Route path="service-records" element={<ServiceRecords />} />
        <Route path="service-records/:id" element={<ServiceRecordDetail />} />
        <Route path="service-timeline" element={<DueList />} />
        <Route path="service-timeline/:id" element={<VehicleTimeline />} />
        <Route path="stock" element={<Stock />} />
        <Route path="expenses" element={<Expenses />} />
        <Route path="service-approvals" element={<ServiceApprovals />} />
        <Route path="settings" element={<SettingsPage />} />
        {Object.values(MASTERS).flatMap((m) => [
          <Route key={m.kind} path={m.path.slice(1)} element={<MasterList key={m.kind} kind={m.kind} />} />,
          <Route key={`${m.kind}-d`} path={`${m.path.slice(1)}/:id`} element={<EntityDetail key={m.kind} kind={m.kind} />} />,
        ])}
        <Route path="m/:key" element={<ModulePlaceholder />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </AppShell>
  );
}

function Protected() {
  const { me, loading } = useSession();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="app-loading">
        <Loader2 className="h-6 w-6 animate-spin text-gold" />
      </div>
    );
  }
  if (!me) {
    return <Navigate to="/sign-in" replace />;
  }
  return <SessionGate />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SessionProvider>
          <Routes>
            <Route path="/sign-in/*" element={<AuthPage mode="sign-in" />} />
            <Route path="/sign-up/*" element={<AuthPage mode="sign-up" />} />
            <Route path="/*" element={<Protected />} />
          </Routes>
        </SessionProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
